import React, { useState, useCallback } from 'react';
import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';

// Authentication
import { AuthProvider } from './contexts/AuthContext';
import { BottomNavProvider } from './contexts/BottomNavContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/auth/AdminRoute';
import OptionalAuthRoute from './components/auth/OptionalAuthRoute';
import LoginForm from './components/auth/LoginForm';
import RegisterForm from './components/auth/RegisterForm';
import ForgotPasswordForm from './components/auth/ForgotPasswordForm';

// Pages
import WelcomePage from './pages/WelcomePage';
import OpsPage from './pages/OpsPage';
import BusinessPage from './pages/BusinessPage';
import BusinessStartPage from './pages/BusinessStartPage';
import LegalPage from './pages/LegalPage';
import PlayPage from './pages/PlayPage';
import JoinPage from './pages/JoinPage';
import PartnerPage from './pages/PartnerPage';
import PartnerDashboard from './pages/PartnerDashboard';
import NewProjectPage from './pages/NewProjectPage';
import OnboardingPage from './pages/OnboardingPage';
import UserProfile from './pages/UserProfile';
import PublicUserProfile from './pages/PublicUserProfile';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import DeleteAccountInfoPage from './pages/DeleteAccountInfoPage';
import NotFound from './pages/NotFound';
import ChatPage from './pages/ChatPage';

// Components
import Layout from './components/Layout';
import AnimatedRoutes from './components/AnimatedRoutes';
import ErrorBoundary from './components/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';
import OfflineIndicator from './components/OfflineIndicator';
import PaywallModal from './components/PaywallModal';
import BackgroundLocationDisclosure from './components/BackgroundLocationDisclosure';
import { BackgroundLocation } from './services/LocationService';

// Styles
import './index.css';
import SavedPage from './pages/SavedPage';
import FinishedProjectImagesPage from './pages/FinishedProjectImagesPage';
import ThingsExplorer from './thingsdb/pages/ThingsExplorer';
import { loadBranding } from './services/branding';
import { useBrandingStore } from './stores/brandingStore';
import AdminLayout from './components/admin/AdminLayout';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminPartnersPage from './pages/AdminPartnersPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminTokenLedgerPage from './pages/AdminTokenLedgerPage';
import AdminVouchersPage from './pages/AdminVouchersPage';
import AdminChatsPage from './pages/AdminChatsPage';
import AdminChatDetailPage from './pages/AdminChatDetailPage';
import AdminReportsPage from './pages/AdminReportsPage';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 10000),
      staleTime: 2 * 60 * 1000, // 2 minutes
      cacheTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
  },
});

function App() {
  const setBranding = useBrandingStore((s) => s.setBranding);
  const [showLocationDisclosure, setShowLocationDisclosure] = useState(false);

  useEffect(() => {
    (async () => {
      const data = await loadBranding();
      if (data) setBranding(data);
    })();
  }, [setBranding]);

  useEffect(() => {
    let cancelled = false;
    const cleanupOrphanedBackgroundWatcher = async () => {
      try {
        const hasClaimedTracker = Number(window.__okio_live_track_claims__ || 0) > 0;
        const storedWatcherId = BackgroundLocation.getStoredWatcherId();
        if (!hasClaimedTracker && storedWatcherId) {
          await BackgroundLocation.stop(storedWatcherId);
        }
      } catch {}
    };

    const timeoutId = window.setTimeout(() => {
      if (cancelled) return;
      cleanupOrphanedBackgroundWatcher();
    }, 2500);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, []);

  // Listen for disclosure requests from useLiveTrack or other location consumers
  useEffect(() => {
    const handler = () => setShowLocationDisclosure(true);
    window.addEventListener('okio:request_location_disclosure', handler);
    return () => window.removeEventListener('okio:request_location_disclosure', handler);
  }, []);

  const handleDisclosureAccept = useCallback(() => {
    setShowLocationDisclosure(false);
    window.dispatchEvent(new CustomEvent('okio:bg_location_consent_granted'));
  }, []);

  const handleDisclosureDecline = useCallback(() => {
    setShowLocationDisclosure(false);
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <BottomNavProvider>
              <Router>
            <ScrollToTop />
            <OfflineIndicator />
            <PaywallModal />
            <BackgroundLocationDisclosure
              isOpen={showLocationDisclosure}
              onAccept={handleDisclosureAccept}
              onDecline={handleDisclosureDecline}
            />
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginForm />} />
              <Route path="/register" element={<RegisterForm />} />
              <Route path="/forgot" element={<ForgotPasswordForm />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/delete-account-info" element={<DeleteAccountInfoPage />} />

              {/* B2B storefront (i18n EN/DE) + legal pages: English routes
                  canonical, German paths kept as aliases (plan_b2b_self_serve.md) */}
              <Route path="/business" element={<BusinessPage />} />
              <Route path="/business/start" element={<BusinessStartPage />} />
              <Route path="/imprint" element={<LegalPage doc="impressum" />} />
              <Route path="/privacy" element={<LegalPage doc="datenschutz" />} />
              <Route path="/terms" element={<LegalPage doc="agb" />} />
              <Route path="/impressum" element={<LegalPage doc="impressum" />} />
              <Route path="/datenschutz" element={<LegalPage doc="datenschutz" />} />
              <Route path="/agb" element={<LegalPage doc="agb" />} />
              
              {/* Home page - default for logged-in users */}
              <Route path="/" element={
                <OptionalAuthRoute>
                  <Layout>
                    <WelcomePage />
                  </Layout>
                </OptionalAuthRoute>
              } />
              
              {/* Protected routes */}
              
              {/* Research-focused routes */}
              <Route path="/projects" element={
                <ProtectedRoute>
                  <AnimatedRoutes />
                </ProtectedRoute>
              } />
              <Route path="/new" element={
                <ProtectedRoute>
                  <Layout>
                    <NewProjectPage />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/onboarding" element={
                <ProtectedRoute allowIncompleteOnboarding>
                  <OnboardingPage />
                </ProtectedRoute>
              } />
              
              {/* IMPORTANT: More specific routes MUST come before general :projectId route */}
              <Route path="/projects/:projectId/images" element={
                <ProtectedRoute>
                  <Layout>
                    <FinishedProjectImagesPage />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* General project route - must be LAST among /projects/* routes */}
              <Route path="/projects/:projectId" element={
                <ProtectedRoute>
                  <AnimatedRoutes />
                </ProtectedRoute>
              } />
              
              <Route path="/projects/:projectId/chats/:chatId" element={
                <ProtectedRoute>
                  <Layout>
                    <ChatPage />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/profile" element={
                <ProtectedRoute>
                  <Layout>
                    <UserProfile />
                  </Layout>
                </ProtectedRoute>
              } />
              
              {/* Public user profile route */}
              <Route path="/users/:username" element={
                <OptionalAuthRoute>
                  <Layout>
                    <PublicUserProfile />
                  </Layout>
                </OptionalAuthRoute>
              } />
              
              <Route path="/starters" element={
                <OptionalAuthRoute>
                  <AnimatedRoutes />
                </OptionalAuthRoute>
              } />
              
              <Route path="/starters/:projectId" element={
                <OptionalAuthRoute>
                  <AnimatedRoutes />
                </OptionalAuthRoute>
              } />

              <Route path="/starters/:projectId/images" element={
                <OptionalAuthRoute>
                  <AnimatedRoutes />
                </OptionalAuthRoute>
              } />
              
              {/* Discover page - public projects shared by the community */}
              <Route path="/discover" element={
                <OptionalAuthRoute>
                  <AnimatedRoutes />
                </OptionalAuthRoute>
              } />
              {/* Observation detail - must come BEFORE general :projectId route */}
              <Route path="/discover/observation/:detectionId" element={
                <OptionalAuthRoute>
                  <AnimatedRoutes />
                </OptionalAuthRoute>
              } />
              <Route path="/discover/:projectId" element={
                <OptionalAuthRoute>
                  <AnimatedRoutes />
                </OptionalAuthRoute>
              } />

              {/* ThingsDB explorer (feature-gated server-side) */}
              <Route path="/things" element={
                <ProtectedRoute>
                  <Layout>
                    <ThingsExplorer />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Admin routes (superuser-only) */}
              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <AdminLayout>
                      <AdminDashboardPage />
                    </AdminLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <AdminRoute>
                    <AdminLayout>
                      <AdminUsersPage />
                    </AdminLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/partners"
                element={
                  <AdminRoute>
                    <AdminLayout>
                      <AdminPartnersPage />
                    </AdminLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/token-ledger"
                element={
                  <AdminRoute>
                    <AdminLayout>
                      <AdminTokenLedgerPage />
                    </AdminLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/vouchers"
                element={
                  <AdminRoute>
                    <AdminLayout>
                      <AdminVouchersPage />
                    </AdminLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/chats"
                element={
                  <AdminRoute>
                    <AdminLayout>
                      <AdminChatsPage />
                    </AdminLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/chats/:chatId"
                element={
                  <AdminRoute>
                    <AdminLayout>
                      <AdminChatDetailPage />
                    </AdminLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/reports"
                element={
                  <AdminRoute>
                    <AdminLayout>
                      <AdminReportsPage />
                    </AdminLayout>
                  </AdminRoute>
                }
              />
              
              {/* Saved pages with animations */}
              <Route path="/saved" element={
                <ProtectedRoute>
                   <Layout>
                    <SavedPage />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/saved/:listId" element={
                <ProtectedRoute>
                  <AnimatedRoutes />
                </ProtectedRoute>
              } />
              
              {/* Welcome page - Landing page with custom header (no Layout wrapper) */}
              <Route path="/welcome" element={
                <WelcomePage />
              } />

              {/* Scan to Play deep link - one URL into a live guided session */}
              <Route path="/play/:starterId" element={
                <PlayPage />
              } />

              {/* Join my run - companion QR/link into an existing session */}
              <Route path="/join/:code" element={
                <JoinPage />
              } />

              {/* Partner page - public venue hub (Scan to Play) */}
              <Route path="/p/:slug" element={
                <PartnerPage />
              } />

              {/* Partner dashboard - stats, QR codes, moderation (admin-only) */}
              <Route path="/p/:slug/dashboard" element={
                <PartnerDashboard />
              } />

              {/* Ops page - Landing page for B2B operations */}
              <Route path="/ops" element={
                  <OpsPage />
              } />
              
              {/* 404 page */}
              <Route path="*" element={<NotFound />} />
            </Routes>
                </Router>
              </BottomNavProvider>
            </AuthProvider>
          </QueryClientProvider>
        </ThemeProvider>
        
        {/* React Query DevTools - Only in development */}
        {/* {process.env.NODE_ENV === 'development' && (
          <React.Suspense fallback={null}>
            <ReactQueryDevtools initialIsOpen={false} />
          </React.Suspense>
        )} */}
        
        {/* Toast notifications */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
              fontFamily: 'Inter, sans-serif',
            },
            success: {
              iconTheme: {
                primary: '#10B981',
                secondary: '#fff',
              },
            },
            error: {
              iconTheme: {
                primary: '#EF4444',
                secondary: '#fff',
              },
            },
          }}
        />
    </ErrorBoundary>
  );
}

export default App; 